import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleUserRound, Coins, LogOut } from 'lucide-react';
import api from '../api';
import { useAuthStore } from '../store/useStore';
import Modal from './Modal';

/**
 * 用户菜单（Header 头像下拉面板）
 * 头像旁边展示默认头像，点击展开：用户信息（昵称 + 手机号）、橡树果明细（余额）、退出登录
 */
export default function UserMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const loadBalance = useCallback(async () => {
    try {
      const res = await api.billing.getAccount();
      setBalance(res.availableAmount);
    } catch (error) {
      // 余额获取失败静默处理，避免打断用户操作
      console.error('Load billing account failed:', error);
      setBalance(null);
    }
  }, []);

  const toggleOpen = () => {
    if (!open) loadBalance();
    setOpen(!open);
  };

  const close = () => setOpen(false);

  const handleLogoutClick = () => {
    close();
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    setShowLogoutConfirm(false);
    navigate('/login');
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* 触发按钮：默认头像 */}
      <button
        onClick={toggleOpen}
        className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-brand/30 transition-all shrink-0"
        aria-label="用户菜单"
      >
        <CircleUserRound size={20} className="text-slate-500" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={close} />
          <div className="dropdown-enter absolute right-0 top-full mt-2.5 w-64 bg-white rounded-2xl shadow-dropdown overflow-hidden z-40">
            {/* 用户信息 */}
            <div className="flex items-center gap-3 px-4 py-4 border-b border-border-subtle">
              <div className="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                <CircleUserRound size={24} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">{user?.nickname || 'ODance用户'}</p>
                <p className="text-xs text-text-muted mt-0.5">{user?.phone || '—'}</p>
              </div>
            </div>

            {/* 橡树果明细 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
              <span className="flex items-center gap-2 text-[13px] text-text-secondary">
                <Coins size={16} className="text-brand" />
                橡树果明细
              </span>
              <span className="text-[13px] font-semibold text-text-primary">
                {balance === null ? '--' : balance}
              </span>
            </div>

            {/* 退出登录 */}
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-2 px-4 py-3 text-[13px] text-danger hover:bg-danger-50 transition-colors"
            >
              <LogOut size={16} />
              退出登录
            </button>
          </div>
        </>
      )}

      <Modal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="退出登录"
        width="max-w-[360px]"
      >
        <div className="space-y-6">
          <p className="text-[14px] text-text-secondary leading-relaxed">
            确定要退出登录吗？退出后将无法继续管理您的舞蹈。
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setShowLogoutConfirm(false)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-border-subtle text-[13px] font-medium text-text-secondary hover:bg-slate-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={confirmLogout}
              className="flex-1 px-4 py-2.5 rounded-xl bg-danger text-white text-[13px] font-medium hover:bg-danger-600 transition-colors shadow-sm shadow-danger/10"
            >
              确定退出
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
